import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LoaderSpinner } from "../../components/LoaderSpinner";
import { SelectDropdown } from "../../components/SelectDropdown";
import { Cog6ToothIcon, EllipsisVerticalIcon, ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from "@headlessui/react";
import { CardTotal } from "../../components/quickbooks/CardTotal";
import { ProfitReportSidebar } from "../../components/quickbooks/ProfitReportSidebar";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../consts/general";
import { getProfitAndLossNewTableTitle } from "../../consts/forecastPlanning";
import { DatePickerInput } from "../../components/DatePickerInput";
import { MonthPickerInput } from "../../components/MonthPickerInput";
import { getEndOfCurrentCalendarYearYmd, getRangeForType } from "../../helpers/profitAndLoss/common";
import { getCompareOptions, getProfitAndLossNewData, getProfitAndLossSnapshotData, getProfitAndLossSnapshotOptions, getProfitAndLossTables, ProfitAndLossNewLoadingKeys } from "../../slices/quickbooks/profitAndLossNewSlice";
import { ProfitLossTable } from "../../components/quickbooks/ProfitLossTable";
import { ProfitAndLossStatementTable } from "../../components/quickbooks/ProfitAndLossStatementTable";
import { ProfitAndLossSettingsSidebar } from "../../components/quickbooks/ProfitAndLossSettingsSidebar";
import { useIsMobile } from "../../helpers/useIsMobile";
import { endOfMonth, format, parseISO, subMonths } from "date-fns";
import {
  HIGHER_IS_BETTER,
  LOWER_IS_BETTER,
} from "../../helpers/profitAndLoss/varianceDirection";

const SNAPSHOT_CUSTOM_DATES = "custom-dates";

const formatSnapshotRangeLabel = (startDate, endDate) => {
  if (!startDate || !endDate) return "";
  const start = parseISO(`${startDate}T00:00:00`);
  const end = parseISO(`${endDate}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "";
  return format(start, "MMMM yyyy");
};

const formatDateRangeLabel = (startDate, endDate) => {
  if (!startDate || !endDate) return "";
  const start = parseISO(`${startDate}T00:00:00`);
  const end = parseISO(`${endDate}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "";
  return `${format(start, "MMM d, yyyy")} \u2013 ${format(end, "MMM d, yyyy")}`;
};

const standardPayloadHasData = (payload) => {
  const months = payload?.meta?.months || [];
  if (!months.length) return false;
  return (payload?.rows || []).some((row) => (
    row.kind !== "section"
    && (
      (row.total !== null && row.total !== undefined)
      || months.some((month) => row.months?.[month.key] !== null && row.months?.[month.key] !== undefined)
    )
  ));
};

const ProfitAndLossNew = () => {
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const pageParams = useMemo(
      () => (window?.location?.search ? new URLSearchParams(window?.location?.search) : null),
      []
    );
    const dispatch = useDispatch();
  
    const [sidebarData, setSidebarData] = useState(null);
    // "standard" selects configured tables; "legacy" selects the QBO statement.
    const [statementView, setStatementView] = useState(pageParams?.get('view') === "legacy" ? "legacy" : "standard");
    const [viewMode, setViewMode] = useState(
      pageParams?.get('view') !== "legacy" && pageParams?.get('mode') === "snapshot" ? "snapshot" : "live"
    );
    const [type, setType] = useState(pageParams?.get('type') || "this-year-to-date");
    const [selectedMonth, setSelectedMonth] = useState(
      pageParams?.get('type') === "select-month" && pageParams?.get('startDate')
        ? pageParams.get('startDate').slice(0, 7)
        : ""
    );
    const [startDate, setStartDate] = useState(pageParams?.get('startDate') || "");
    const [endDate, setEndDate] = useState(pageParams?.get('endDate') || "");
    const [compareType, setCompareType] = useState(pageParams?.get('compareType') || "none");
    const [snapshotType, setSnapshotType] = useState(pageParams?.get('snapshotType') || "");
    const [snapshotStartDate, setSnapshotStartDate] = useState(pageParams?.get('snapshotStartDate') || "");
    const [snapshotEndDate, setSnapshotEndDate] = useState(pageParams?.get('snapshotEndDate') || pageParams?.get('snapshotDate') || "");
    const [mobilePeriodDialogType, setMobilePeriodDialogType] = useState(null);
    const [mobileDraftStartDate, setMobileDraftStartDate] = useState("");
    const [mobileDraftEndDate, setMobileDraftEndDate] = useState("");
    const [mobileDraftMonth, setMobileDraftMonth] = useState("");
    const [exportingTable, setExportingTable] = useState(null);
    const [isExporting, setIsExporting] = useState(false);
    const [standardStatement, setStandardStatement] = useState(null);
    const [standardStatementLoading, setStandardStatementLoading] = useState(false);
    const [standardStatementError, setStandardStatementError] = useState("");
    const [standardStatementExporting, setStandardStatementExporting] = useState(false);
    const [settingsIsOpen, setSettingsIsOpen] = useState(false);

    const {
      compareOptions,
      snapshotOptions,
      profitAndLossNewData,
      loadingKeys,
    } = useSelector((state) => state.profitAndLossNew);

    const snapshotPeriodOptions = useMemo(() => {
      const groupedByMonth = new Map();
      for (const option of snapshotOptions || []) {
        const monthKey = option?.snapshotMonth || option?.value;
        if (!monthKey) continue;
        if (!groupedByMonth.has(monthKey)) {
          groupedByMonth.set(monthKey, {
            value: monthKey,
            label: formatSnapshotRangeLabel(option?.monthStartDate, option?.monthEndDate),
            snapshotDate: option?.value,
            monthStartDate: option?.monthStartDate,
            monthEndDate: option?.monthEndDate,
          });
          continue;
        }
        const existing = groupedByMonth.get(monthKey);
        if (String(option?.value || "") > String(existing?.snapshotDate || "")) {
          groupedByMonth.set(monthKey, {
            ...existing,
            snapshotDate: option?.value,
          });
        }
      }
      return Array.from(groupedByMonth.values()).sort((a, b) => String(b.snapshotDate || "").localeCompare(String(a.snapshotDate || "")));
    }, [snapshotOptions]);

    const selectedSnapshotPeriod = useMemo(() => {
      return snapshotPeriodOptions.find((option) => option.value === snapshotType) || null;
    }, [snapshotPeriodOptions, snapshotType]);

    const availableSnapshotDates = useMemo(() => {
      return (snapshotOptions || []).map((option) => option?.value).filter(Boolean).sort((a, b) => String(a).localeCompare(String(b)));
    }, [snapshotOptions]);

    const minSnapshotDate = availableSnapshotDates?.[0] || "2024-01-01";
    const maxSnapshotDate = availableSnapshotDates?.[availableSnapshotDates.length - 1] || "";
    const maxCalendarYearEndYmd = getEndOfCurrentCalendarYearYmd();
    const snapshotEndPickerMax = maxSnapshotDate
      ? (maxSnapshotDate < maxCalendarYearEndYmd ? maxSnapshotDate : maxCalendarYearEndYmd)
      : maxCalendarYearEndYmd;
    const snapshotStartPickerMax = snapshotEndDate
      ? (snapshotEndDate < maxCalendarYearEndYmd ? snapshotEndDate : maxCalendarYearEndYmd)
      : maxCalendarYearEndYmd;

    const activeSnapshotDate = useMemo(() => {
      if (snapshotType === SNAPSHOT_CUSTOM_DATES) return snapshotEndDate;
      return selectedSnapshotPeriod?.snapshotDate || "";
    }, [selectedSnapshotPeriod, snapshotEndDate, snapshotType]);

    const snapshotQueryRange = useMemo(() => {
      if (snapshotType !== SNAPSHOT_CUSTOM_DATES) return null;
      if (!snapshotStartDate || !snapshotEndDate) return null;
      return {
        startDate: snapshotStartDate,
        endDate: snapshotEndDate,
      };
    }, [snapshotEndDate, snapshotStartDate, snapshotType]);

    const tableTitleType = viewMode === "snapshot" ? snapshotOptions?.find((option) => option.value === snapshotType)?.label || "" : type;
    const tableTitleStartDate = viewMode === "snapshot" ? null : startDate;
    const tableTitleEndDate = viewMode === "snapshot" ? null : endDate;
    const activeDateRangeLabel = useMemo(() => {
      const range = viewMode === "snapshot"
        ? (snapshotQueryRange || {
            startDate: selectedSnapshotPeriod?.monthStartDate,
            endDate: selectedSnapshotPeriod?.monthEndDate,
          })
        : { startDate, endDate };
      return formatDateRangeLabel(range?.startDate, range?.endDate);
    }, [
      endDate,
      selectedSnapshotPeriod,
      snapshotQueryRange,
      startDate,
      viewMode,
    ]);

    const refreshProfitAndLossData = useCallback(() => {
      if (!startDate || !endDate) return;
      // The Compare selector only exists in the standard view.
      const compareTypeIsNotEmpty = statementView === "standard" && compareType && compareType !== "none";
      dispatch(getProfitAndLossNewData({
        startDate,
        endDate,
        compareType: compareTypeIsNotEmpty ? compareType : null,
      }));
    }, [compareType, dispatch, endDate, startDate, statementView]);

    const refreshSnapshotData = useCallback(() => {
      if (snapshotQueryRange?.startDate && snapshotQueryRange?.endDate) {
        dispatch(getProfitAndLossSnapshotData({
          startDate: snapshotQueryRange.startDate,
          endDate: snapshotQueryRange.endDate,
        }));
        return;
      }
      if (!activeSnapshotDate) return;
      dispatch(getProfitAndLossSnapshotData({
        snapshotDate: activeSnapshotDate,
      }));
    }, [activeSnapshotDate, dispatch, snapshotQueryRange]);

    const hasUrlDateParams = useMemo(() => {
      const hasType = !!pageParams?.get("type");
      const hasStart = !!pageParams?.get("startDate");
      const hasEnd = !!pageParams?.get("endDate");
      return hasType || hasStart || hasEnd;
    }, [pageParams]);

    useEffect(() => {
      dispatch(getProfitAndLossSnapshotOptions({}));
      if (statementView !== "standard") return;
      dispatch(getCompareOptions({}));
      dispatch(getProfitAndLossTables({}));
    }, [dispatch, statementView]);

    useEffect(() => {
      if (viewMode === "snapshot") return;
      if (hasUrlDateParams) return;
      if (type !== "this-year-to-date") return;
      if (startDate || endDate) return;
      const range = getRangeForType("this-year-to-date");
      if (range) {
        setStartDate(range.start);
        setEndDate(range.end);
      }
    }, [endDate, hasUrlDateParams, startDate, type, viewMode]);

    useEffect(() => {
      if (viewMode !== "live") return;
      if (!startDate || !endDate) return;
      const compareTypeIsNotEmpty = compareType && compareType !== "none";
      const params = new URLSearchParams({
        mode: "live",
        type,
        startDate,
        endDate,
        compareType: compareTypeIsNotEmpty ? compareType : "",
      });
      if (statementView === "legacy") params.set("view", "legacy");
      refreshProfitAndLossData();
      navigate(`${ROUTES.PROFIT_AND_LOSS}?${params}`, { replace: true });
    }, [
      type,
      startDate,
      endDate,
      compareType,
      statementView,
      viewMode,
      refreshProfitAndLossData,
      navigate,
    ]);

    const refreshStandardStatement = useCallback(() => {
      if (statementView !== "legacy" || !startDate || !endDate) return () => {};
      const controller = new AbortController();
      setStandardStatementLoading(true);
      setStandardStatementError("");
      fetch(`/api/v1/profit-and-loss-new/statement?${new URLSearchParams({ startDate, endDate })}`, {
        signal: controller.signal,
      })
        .then((response) => {
          if (!response.ok) throw new Error("Failed to load standard Profit & Loss statement");
          return response.json();
        })
        .then((payload) => {
          if (!controller.signal.aborted) setStandardStatement(payload);
        })
        .catch((error) => {
          if (!controller.signal.aborted) {
            setStandardStatementError(error.message || "Failed to load standard Profit & Loss statement");
          }
        })
        .finally(() => {
          if (!controller.signal.aborted) setStandardStatementLoading(false);
        });
      return () => controller.abort();
    }, [endDate, startDate, statementView]);

    useEffect(() => {
      const cancel = refreshStandardStatement();
      return () => {
        cancel();
      };
    }, [refreshStandardStatement]);

    useEffect(() => {
      if (viewMode !== "snapshot") return;
      if (snapshotType) return;
      const firstSnapshotPeriod = snapshotPeriodOptions?.[0];
      if (!firstSnapshotPeriod) return;
      setSnapshotType(firstSnapshotPeriod.value);
      setSnapshotStartDate(firstSnapshotPeriod.monthStartDate || "");
      setSnapshotEndDate(firstSnapshotPeriod.monthEndDate || firstSnapshotPeriod.snapshotDate || "");
    }, [snapshotPeriodOptions, snapshotType, viewMode]);

    useEffect(() => {
      if (viewMode !== "snapshot") return;
      if (snapshotType === SNAPSHOT_CUSTOM_DATES) return;
      if (!selectedSnapshotPeriod) return;
      if (snapshotStartDate !== selectedSnapshotPeriod.monthStartDate) {
        setSnapshotStartDate(selectedSnapshotPeriod.monthStartDate || "");
      }
      if (snapshotEndDate !== selectedSnapshotPeriod.monthEndDate) {
        setSnapshotEndDate(selectedSnapshotPeriod.monthEndDate || "");
      }
    }, [selectedSnapshotPeriod, snapshotEndDate, snapshotStartDate, snapshotType, viewMode]);

    useEffect(() => {
      if (viewMode !== "snapshot") return;
      if (!activeSnapshotDate && !snapshotQueryRange) return;
      const params = new URLSearchParams({
        mode: "snapshot",
        snapshotType,
        snapshotStartDate,
        snapshotEndDate,
        snapshotDate: activeSnapshotDate,
      });
      refreshSnapshotData();
      navigate(`${ROUTES.PROFIT_AND_LOSS}?${params}`, { replace: true });
    }, [viewMode, snapshotType, snapshotStartDate, snapshotEndDate, activeSnapshotDate, snapshotQueryRange, refreshSnapshotData, navigate]);

    const getMonthRange = (monthValue) => {
      if (!monthValue) return null;
      const monthStart = parseISO(`${monthValue}-01`);
      if (Number.isNaN(monthStart.getTime())) return null;
      return {
        start: format(monthStart, "yyyy-MM-dd"),
        end: format(endOfMonth(monthStart), "yyyy-MM-dd"),
      };
    };

    const handleMonthChange = (nextMonth) => {
      setSelectedMonth(nextMonth);
      const range = getMonthRange(nextMonth);
      if (range) {
        setStartDate(range.start);
        setEndDate(range.end);
      }
    };

    const applyType = (nextType) => {
      setType(nextType);
      if (nextType === "custom-dates") return;
      if (nextType === "select-month") {
        const defaultMonth = selectedMonth || format(subMonths(new Date(), 1), "yyyy-MM");
        handleMonthChange(defaultMonth);
        return;
      }
      const range = getRangeForType(nextType);
      if (range) {
        setStartDate(range.start);
        setEndDate(range.end);
      }
    };

    const navigateLiveState = ({
      nextStatementView = statementView,
      nextType = type,
      nextStartDate = startDate,
      nextEndDate = endDate,
      nextCompareType = compareType,
    } = {}) => {
      const compareTypeIsNotEmpty = nextCompareType && nextCompareType !== "none";
      const params = new URLSearchParams({
        mode: "live",
        type: nextType,
        startDate: nextStartDate,
        endDate: nextEndDate,
        compareType: compareTypeIsNotEmpty ? nextCompareType : "",
      });
      if (nextStatementView === "legacy") params.set("view", "legacy");
      navigate(`${ROUTES.PROFIT_AND_LOSS}?${params}`, { replace: true });
    };

    const navigateSnapshotState = ({
      nextSnapshotType,
      nextSnapshotStartDate,
      nextSnapshotEndDate,
      nextSnapshotDate,
    }) => {
      const params = new URLSearchParams({
        mode: "snapshot",
        snapshotType: nextSnapshotType,
        snapshotStartDate: nextSnapshotStartDate,
        snapshotEndDate: nextSnapshotEndDate,
        snapshotDate: nextSnapshotDate,
      });
      navigate(`${ROUTES.PROFIT_AND_LOSS}?${params}`, { replace: true });
    };

    const handleViewModeChange = (nextMode) => {
      if (nextMode === viewMode && !(nextMode === "snapshot" && statementView === "legacy")) return;
      if (nextMode === "snapshot") {
        const firstSnapshotPeriod = snapshotPeriodOptions?.[0];
        if (!firstSnapshotPeriod) return;
        const nextSnapshotType = firstSnapshotPeriod.value;
        const nextSnapshotStartDate = firstSnapshotPeriod.monthStartDate || "";
        const nextSnapshotEndDate = firstSnapshotPeriod.monthEndDate || firstSnapshotPeriod.snapshotDate || "";
        const nextSnapshotDate = firstSnapshotPeriod.snapshotDate || firstSnapshotPeriod.value;
        setStatementView("standard");
        setViewMode("snapshot");
        setSnapshotType(nextSnapshotType);
        setSnapshotStartDate(nextSnapshotStartDate);
        setSnapshotEndDate(nextSnapshotEndDate);
        navigateSnapshotState({
          nextSnapshotType,
          nextSnapshotStartDate,
          nextSnapshotEndDate,
          nextSnapshotDate,
        });
        return;
      }
      setStatementView("standard");
      setViewMode("live");
      const nextRange = startDate && endDate
        ? { start: startDate, end: endDate }
        : getRangeForType(type === "custom-dates" ? "this-year-to-date" : type);
      if (nextRange) {
        setStartDate(nextRange.start);
        setEndDate(nextRange.end);
      }
      navigateLiveState({
        nextStatementView: "standard",
        nextStartDate: nextRange?.start || startDate,
        nextEndDate: nextRange?.end || endDate,
      });
    };

    const applySnapshotType = (nextType) => {
      setSnapshotType(nextType);
      if (nextType === SNAPSHOT_CUSTOM_DATES) return;
      const snapshotPeriod = snapshotPeriodOptions.find((option) => option.value === nextType);
      if (!snapshotPeriod) return;
      setSnapshotStartDate(snapshotPeriod.monthStartDate || "");
      setSnapshotEndDate(snapshotPeriod.monthEndDate || snapshotPeriod.snapshotDate || "");
    };

    const openMobilePeriodDialog = (nextType) => {
      setMobileDraftStartDate(viewMode === "snapshot" ? snapshotStartDate : startDate);
      setMobileDraftEndDate(viewMode === "snapshot" ? snapshotEndDate : endDate);
      setMobileDraftMonth(
        selectedMonth
        || (startDate ? startDate.slice(0, 7) : "")
        || format(subMonths(new Date(), 1), "yyyy-MM")
      );
      setMobilePeriodDialogType(nextType);
    };

    const handleMobilePeriodTypeChange = (nextType) => {
      if (nextType === SNAPSHOT_CUSTOM_DATES || nextType === "select-month") {
        openMobilePeriodDialog(nextType);
        return;
      }
      if (viewMode === "snapshot") {
        applySnapshotType(nextType);
        return;
      }
      applyType(nextType);
    };

    const MIN_ALLOWED_DATE = "2024-01-01";
    const clampToMinAllowed = (ymd) => {
      if (!ymd) return ymd;
      return ymd < MIN_ALLOWED_DATE ? MIN_ALLOWED_DATE : ymd;
    };

    const handleStartDateChange = (nextStart) => {
      const clamped = clampToMinAllowed(nextStart);
      setStartDate(clamped);
      if (endDate && nextStart && nextStart > endDate) {
        setEndDate(clamped);
      }
    };

    const handleEndDateChange = (nextEnd) => {
      const clamped = clampToMinAllowed(nextEnd);
      setEndDate(clamped);
      if (startDate && nextEnd && nextEnd < startDate) {
        setStartDate(clamped);
      }
    };

    const handleSnapshotStartDateChange = (nextStart) => {
      const clamped = clampToMinAllowed(nextStart);
      setSnapshotStartDate(clamped);
      if (snapshotEndDate && nextStart && nextStart > snapshotEndDate) {
        setSnapshotEndDate(clamped);
      }
    };

    const handleSnapshotEndDateChange = (nextEnd) => {
      const clamped = clampToMinAllowed(nextEnd);
      setSnapshotEndDate(clamped);
      if (snapshotStartDate && nextEnd && nextEnd < snapshotStartDate) {
        setSnapshotStartDate(clamped);
      }
    };

    const handleMobileDraftStartDateChange = (nextStart) => {
      const clamped = clampToMinAllowed(nextStart);
      setMobileDraftStartDate(clamped);
      if (mobileDraftEndDate && nextStart && nextStart > mobileDraftEndDate) {
        setMobileDraftEndDate(clamped);
      }
    };

    const handleMobileDraftEndDateChange = (nextEnd) => {
      const clamped = clampToMinAllowed(nextEnd);
      setMobileDraftEndDate(clamped);
      if (mobileDraftStartDate && nextEnd && nextEnd < mobileDraftStartDate) {
        setMobileDraftStartDate(clamped);
      }
    };

    const handleMobilePeriodDialogApply = () => {
      if (viewMode === "snapshot" && mobilePeriodDialogType === SNAPSHOT_CUSTOM_DATES) {
        if (!mobileDraftStartDate || !mobileDraftEndDate) return;
        setSnapshotType(SNAPSHOT_CUSTOM_DATES);
        setSnapshotStartDate(mobileDraftStartDate);
        setSnapshotEndDate(mobileDraftEndDate);
      } else if (mobilePeriodDialogType === "select-month") {
        if (!mobileDraftMonth) return;
        setType("select-month");
        handleMonthChange(mobileDraftMonth);
      } else {
        if (!mobileDraftStartDate || !mobileDraftEndDate) return;
        setType("custom-dates");
        setStartDate(mobileDraftStartDate);
        setEndDate(mobileDraftEndDate);
      }
      setMobilePeriodDialogType(null);
    };

    const handleExport = async (tableKey) => {
      if (viewMode === "live" && (!startDate || !endDate)) return;
      if (viewMode === "snapshot" && !activeSnapshotDate && !snapshotQueryRange) return;
      try {
        setExportingTable(tableKey);
        const compareTypeIsNotEmpty = compareType && compareType !== "none";
        const params = new URLSearchParams(
          viewMode === "snapshot"
            ? (
                snapshotQueryRange
                  ? {
                      startDate: snapshotQueryRange.startDate,
                      endDate: snapshotQueryRange.endDate,
                      table: tableKey,
                    }
                  : {
                      snapshotDate: activeSnapshotDate,
                      table: tableKey,
                    }
              )
            : {
                startDate,
                endDate,
                table: tableKey,
                ...(compareTypeIsNotEmpty ? { compareType } : {}),
              }
        );
        const endpoint = viewMode === "snapshot"
          ? `/api/v1/profit-and-loss-new/snapshots/export?${params}`
          : `/api/v1/profit-and-loss-new/export?${params}`;
        const response = await fetch(endpoint);
        if (!response.ok) throw new Error("Export failed");
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = viewMode === "snapshot"
          ? `profit_and_loss_snapshot_${snapshotQueryRange ? `${snapshotQueryRange.startDate}_to_${snapshotQueryRange.endDate}` : activeSnapshotDate}_${tableKey}.csv`
          : `profit_and_loss_new_${startDate}_to_${endDate}_${tableKey}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Failed to export:", error);
      } finally {
        setExportingTable(null);
      }
    };

    const handleExportAll = async () => {
      if (viewMode === "live" && (!startDate || !endDate)) return;
      if (viewMode === "snapshot" && !activeSnapshotDate && !snapshotQueryRange) return;
      try {
        setIsExporting(true);
        const compareTypeIsNotEmpty = compareType && compareType !== "none";
        const params = new URLSearchParams(
          viewMode === "snapshot"
            ? (
                snapshotQueryRange
                  ? {
                      startDate: snapshotQueryRange.startDate,
                      endDate: snapshotQueryRange.endDate,
                    }
                  : {
                      snapshotDate: activeSnapshotDate,
                    }
              )
            : {
                startDate,
                endDate,
                ...(compareTypeIsNotEmpty ? { compareType } : {}),
              }
        );
        const endpoint = viewMode === "snapshot"
          ? `/api/v1/profit-and-loss-new/snapshots/export-all?${params}`
          : `/api/v1/profit-and-loss-new/export-all?${params}`;
        const response = await fetch(endpoint);
        if (!response.ok) throw new Error("Export failed");
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = viewMode === "snapshot"
          ? `profit_and_loss_snapshot_${snapshotQueryRange ? `${snapshotQueryRange.startDate}_to_${snapshotQueryRange.endDate}` : activeSnapshotDate}_all_tables.csv`
          : `profit_and_loss_new_${startDate}_to_${endDate}_all_tables.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Failed to export:", error);
      } finally {
        setIsExporting(false);
      }
    };

    const handleStandardExport = async () => {
      if (!startDate || !endDate) return;
      try {
        setStandardStatementExporting(true);
        const params = new URLSearchParams({ startDate, endDate });
        const response = await fetch(`/api/v1/profit-and-loss-new/statement/export?${params}`);
        if (!response.ok) throw new Error("Export failed");
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `profit_and_loss_${startDate}_to_${endDate}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Failed to export standard Profit & Loss:", error);
      } finally {
        setStandardStatementExporting(false);
      }
    };

    const handleStatementViewChange = (nextView) => {
      if (nextView === statementView) return;
      setStatementView(nextView);
      if (nextView === "standard") {
        if (viewMode === "snapshot") {
          navigateSnapshotState({
            nextSnapshotType: snapshotType,
            nextSnapshotStartDate: snapshotStartDate,
            nextSnapshotEndDate: snapshotEndDate,
            nextSnapshotDate: activeSnapshotDate,
          });
          return;
        }
        const nextRange = startDate && endDate
          ? { start: startDate, end: endDate }
          : getRangeForType(type === "custom-dates" ? "this-year-to-date" : type);
        if (nextRange) {
          setStartDate(nextRange.start);
          setEndDate(nextRange.end);
        }
        navigateLiveState({
          nextStatementView: "standard",
          nextStartDate: nextRange?.start || startDate,
          nextEndDate: nextRange?.end || endDate,
        });
        return;
      }
      if (viewMode === "snapshot") {
        setViewMode("live");
        const nextRange = startDate && endDate
          ? { start: startDate, end: endDate }
          : getRangeForType(type === "custom-dates" ? "this-year-to-date" : type);
        if (nextRange) {
          setStartDate(nextRange.start);
          setEndDate(nextRange.end);
        }
        navigateLiveState({
          nextStatementView: "legacy",
          nextStartDate: nextRange?.start || startDate,
          nextEndDate: nextRange?.end || endDate,
        });
        return;
      }
      navigateLiveState({ nextStatementView: "legacy" });
    };

    const handleOpenBreakdownSidebar = ({
      total,
      items,
      title,
      month
    }) => {
      setSidebarData({
        total,
        items,
        title,
        month
      })
    }

    const totalsData = profitAndLossNewData?.totals || null;
    const tableData = profitAndLossNewData?.tableData || null;
    const totalsByRows = profitAndLossNewData?.totalsByRows || null;
    
    const compareOptionsIsLoading = loadingKeys.includes(ProfitAndLossNewLoadingKeys.getCompareOptions);
    const snapshotOptionsIsLoading = loadingKeys.includes(ProfitAndLossNewLoadingKeys.getProfitAndLossSnapshotOptions);
    const profitAndLossNewDataIsLoading = loadingKeys.includes(ProfitAndLossNewLoadingKeys.getProfitAndLossNewData)
      || loadingKeys.includes(ProfitAndLossNewLoadingKeys.getProfitAndLossSnapshotData);

    const totalsCards = (
      <section className="grid gap-3 my-4 grid-cols-2 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6">
        {totalsData?.["revenue"] && (
          <CardTotal
            data={totalsData?.["revenue"]}
            varianceDirection={HIGHER_IS_BETTER}
            isLoading={profitAndLossNewDataIsLoading}
          />
        )}
        {totalsData?.["contribution-margin"] && (
          <CardTotal
            data={
              isMobile && totalsData?.["contribution-margin"]?.title === "Contribution Margin"
                ? { ...totalsData["contribution-margin"], title: "CM" }
                : totalsData?.["contribution-margin"]
            }
            varianceDirection={HIGHER_IS_BETTER}
            percentageLabel={"Contribution Ratio: "}
            isLoading={profitAndLossNewDataIsLoading}
          />
        )}
        {totalsData?.["net-income"] && (
          <CardTotal
            data={totalsData?.["net-income"]}
            varianceDirection={HIGHER_IS_BETTER}
            percentageLabel={"Net Profit: "}
            isLoading={profitAndLossNewDataIsLoading}
          />
        )}
        {totalsData?.["beroas"] && (
          <CardTotal
            data={totalsData?.["beroas"]}
            varianceDirection={LOWER_IS_BETTER}
            isLoading={profitAndLossNewDataIsLoading}
            withoutTransform={true}
          />
        )}
        {totalsData?.["ad-spend"] && (
          <CardTotal
            data={totalsData?.["ad-spend"]}
            varianceDirection={LOWER_IS_BETTER}
            percentageLabel={"% of Revenue: "}
            isLoading={profitAndLossNewDataIsLoading}
          />
        )}
        {totalsData?.["mer"] && (
          <CardTotal
            data={totalsData?.["mer"]}
            varianceDirection={HIGHER_IS_BETTER}
            isLoading={profitAndLossNewDataIsLoading}
            withoutTransform={true}
          />
        )}
      </section>
    );

    return (
      <>
        <main className={`w-full min-w-0 ${isMobile ? "px-3" : "px-8"}`}>
            {isMobile && (
              <>
                <div className="flex min-w-0 items-center justify-between gap-3 border-b border-gray-200 py-3">
                  <h1 className="min-w-0 truncate text-xl font-bold text-stone-900">
                    {viewMode === "snapshot" ? "Profit & Loss Snapshot" : "Profit & Loss"}
                  </h1>
                  <div className="flex shrink-0 items-center gap-2">
                    <div className="inline-flex overflow-hidden rounded-lg border border-gray-300">
                      <button
                        type="button"
                        onClick={() => handleViewModeChange("live")}
                        aria-label="Live Mode"
                        className={`px-2 py-1.5 text-xs font-semibold ${
                          viewMode === "live"
                            ? "bg-green-100 text-green-800"
                            : "bg-white text-zinc-500"
                        }`}
                      >
                        Live
                      </button>
                      <button
                        type="button"
                        onClick={() => handleViewModeChange("snapshot")}
                        title="Snapshots use the Standard layout."
                        aria-label="Snapshot Mode"
                        disabled={snapshotOptionsIsLoading || (!snapshotOptions?.length && viewMode !== "snapshot")}
                        className={`border-l border-gray-300 px-2 py-1.5 text-xs font-semibold ${
                          viewMode === "snapshot"
                            ? "bg-green-100 text-green-800"
                            : "bg-white text-zinc-500"
                        } disabled:cursor-not-allowed disabled:opacity-50`}
                      >
                        Snap
                      </button>
                    </div>
                    <Menu as="div" className="relative">
                      <MenuButton
                        type="button"
                        aria-label="More Profit & Loss options"
                        className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-gray-300 bg-white text-stone-900 hover:bg-gray-50"
                      >
                        <EllipsisVerticalIcon className="h-5 w-5" />
                      </MenuButton>
                      <MenuItems className="absolute right-0 z-50 mt-1 w-56 origin-top-right rounded-lg border border-gray-200 bg-white p-1 shadow-lg focus:outline-none">
                        <div className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-zinc-500">
                          View
                        </div>
                        <div className="mb-1 flex overflow-hidden rounded-md border border-gray-300">
                          <MenuItem>
                            {({ focus }) => (
                              <button
                                type="button"
                                onClick={() => handleStatementViewChange("standard")}
                                aria-pressed={statementView === "standard"}
                                className={`flex-1 px-2 py-2 text-sm font-medium ${
                                  statementView === "standard"
                                    ? "bg-green-100 text-green-800"
                                    : "bg-white text-zinc-600"
                                } ${focus ? "outline-none ring-2 ring-inset ring-green-500" : ""}`}
                              >
                                Standard
                              </button>
                            )}
                          </MenuItem>
                          <MenuItem>
                            {({ focus }) => (
                              <button
                                type="button"
                                onClick={() => handleStatementViewChange("legacy")}
                                aria-pressed={statementView === "legacy"}
                                className={`flex-1 border-l border-gray-300 px-2 py-2 text-sm font-medium ${
                                  statementView === "legacy"
                                    ? "bg-green-100 text-green-800"
                                    : "bg-white text-zinc-600"
                                } ${focus ? "outline-none ring-2 ring-inset ring-green-500" : ""}`}
                              >
                                Legacy
                              </button>
                            )}
                          </MenuItem>
                        </div>
                        <MenuItem>
                          {({ focus }) => (
                            <button
                              type="button"
                              onClick={() => setSettingsIsOpen(true)}
                              className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium text-stone-900 ${
                                focus ? "bg-gray-100" : ""
                              }`}
                            >
                              <Cog6ToothIcon className="h-4 w-4" />
                              Profit &amp; Loss Settings
                            </button>
                          )}
                        </MenuItem>
                      </MenuItems>
                    </Menu>
                  </div>
                </div>
              </>
            )}
            {!isMobile && (
              <div className="h-auto min-h-14 border-b border-gray-200 py-3 flex gap-2 justify-between items-center flex-row flex-nowrap">
                <div className="flex items-baseline gap-3 min-w-0 flex-nowrap">
                  <h1 className="text-xl font-bold text-stone-900 whitespace-nowrap">
                    {viewMode === "snapshot" ? "Profit & Loss Snapshot" : "Profit & Loss"}
                  </h1>
                  {activeDateRangeLabel && (
                    <span className="text-sm text-zinc-500 whitespace-nowrap">
                      {activeDateRangeLabel}
                    </span>
                  )}
                </div>
                <div className="flex gap-2 items-center shrink-0 flex-row flex-nowrap">
                  <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => handleStatementViewChange("standard")}
                      className={`px-3 py-2 text-sm font-semibold whitespace-nowrap ${
                        statementView === "standard"
                          ? "bg-green-100 text-green-800"
                          : "bg-white text-zinc-500"
                      }`}
                    >
                      Standard
                    </button>
                    <button
                      type="button"
                      onClick={() => handleStatementViewChange("legacy")}
                      className={`border-l border-gray-300 px-3 py-2 text-sm font-semibold whitespace-nowrap ${
                        statementView === "legacy"
                          ? "bg-green-100 text-green-800"
                          : "bg-white text-zinc-500"
                      }`}
                    >
                      Legacy View
                    </button>
                  </div>
                  <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => handleViewModeChange("live")}
                      className={`px-3 py-2 text-sm font-semibold whitespace-nowrap ${
                        viewMode === "live"
                          ? "bg-green-100 text-green-800"
                          : "bg-white text-zinc-500"
                      }`}
                    >
                      Live Mode
                    </button>
                    <button
                      type="button"
                      onClick={() => handleViewModeChange("snapshot")}
                      title="Snapshots use the Standard layout."
                      disabled={snapshotOptionsIsLoading || (!snapshotOptions?.length && viewMode !== "snapshot")}
                      className={`px-3 py-2 text-sm font-semibold border-l border-gray-300 whitespace-nowrap ${
                        viewMode === "snapshot"
                          ? "bg-green-100 text-green-800"
                          : "bg-white text-zinc-500"
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      Snapshot Mode
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSettingsIsOpen(true)}
                    className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-stone-900 hover:bg-gray-50 whitespace-nowrap"
                  >
                    <Cog6ToothIcon className="h-4 w-4" />
                    Profit & Loss Settings
                  </button>
                  <button
                    onClick={statementView === "legacy" ? handleStandardExport : handleExportAll}
                    disabled={statementView === "legacy"
                      ? standardStatementExporting || standardStatementLoading || !startDate || !endDate
                      : isExporting || compareOptionsIsLoading || profitAndLossNewDataIsLoading}
                    className="flex items-center justify-between gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-stone-900 transition-all duration-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {(statementView === "legacy" ? standardStatementExporting : isExporting) ? (
                      <>
                        <LoaderSpinner width="16" height="16" border="2" parentHeight="h-4" />
                        <span>Exporting...</span>
                      </>
                    ) : (
                      <>
                        <ArrowDownTrayIcon className="h-4 w-4" />
                        <span>Export CSV</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
            {!isMobile && (
              <div className="h-auto border-b border-gray-200 py-2 flex flex-col min-w-0">
              <div className="flex flex-row items-center gap-3 flex-wrap text-sm text-zinc-500">
                {viewMode === "snapshot" ? (
                  <>
                    <div className="flex flex-row gap-2 items-center">
                      <span className="text-sm text-zinc-500">Reporting Period:</span>
                      <div className="w-64">
                        <SelectDropdown
                          options={[
                            ...(snapshotPeriodOptions || []),
                            // { value: SNAPSHOT_CUSTOM_DATES, label: "Custom Dates" },
                          ]}
                          value={snapshotType}
                          onChange={applySnapshotType}
                          selectClassname={"!bg-white !border-gray-300 !rounded-lg !text-stone-900"}
                          disabled={snapshotOptionsIsLoading || !snapshotPeriodOptions?.length}
                        />
                      </div>
                    </div>
                    {snapshotType === SNAPSHOT_CUSTOM_DATES && (
                      <>
                        <div className="flex flex-row gap-2 items-center">
                          <div className="w-52">
                            <DatePickerInput
                              value={snapshotStartDate}
                              onChange={handleSnapshotStartDateChange}
                              placeholder="Start date"
                              min={minSnapshotDate}
                              max={snapshotStartPickerMax}
                              allowedDates={availableSnapshotDates}
                              buttonClassName="!rounded-lg !border-gray-300 !bg-white !text-stone-900"
                              disabled={snapshotOptionsIsLoading}
                            />
                          </div>
                        </div>
                        <span>to</span>
                        <div className="flex flex-row gap-2 items-center">
                          <div className="w-52">
                            <DatePickerInput
                              value={snapshotEndDate}
                              onChange={handleSnapshotEndDateChange}
                              placeholder="End date"
                              min={(snapshotStartDate && snapshotStartDate > minSnapshotDate) ? snapshotStartDate : minSnapshotDate}
                              max={snapshotEndPickerMax}
                              allowedDates={availableSnapshotDates}
                              buttonClassName="!rounded-lg !border-gray-300 !bg-white !text-stone-900"
                              disabled={snapshotOptionsIsLoading}
                            />
                          </div>
                        </div>
                      </>
                    )}
                    {!snapshotOptionsIsLoading && !snapshotOptions?.length && (
                      <div className="text-sm text-gray-500">
                        No snapshots available yet. The monthly snapshot cron will populate this list after it runs.
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="flex flex-row gap-2 items-center">
                      <span className="text-sm text-zinc-500">Reporting Period:</span>
                      <div className="w-52">
                        <SelectDropdown
                          options={[
                            { value: "this-year-to-date", label: "This Year to Date" },
                            { value: "last-month", label: "Last Month" },
                            { value: "last-quarter", label: "Last Quarter" },
                            { value: "last-year", label: "Last Year" },
                            { value: "select-month", label: "Select Month" },
                            { value: "custom-dates", label: "Custom Dates" },
                          ]}
                          value={type}
                          onChange={applyType}
                          selectClassname={"!bg-white !border-gray-300 !rounded-lg !text-stone-900"}
                          disabled={compareOptionsIsLoading}
                        />
                      </div>
                    </div>
                    {type === "custom-dates" && (
                      <>
                        <div className="flex flex-row gap-2 items-center">
                          <div className="w-52">
                            <DatePickerInput
                              value={startDate}
                              onChange={handleStartDateChange}
                              placeholder="Start date"
                              min={"2024-01-01"}
                              max={endDate || undefined}
                              buttonClassName="!rounded-lg !border-gray-300 !bg-white !text-stone-900"
                              disabled={compareOptionsIsLoading}
                            />
                          </div>
                        </div>
                        <span>to</span>
                        <div className="flex flex-row gap-2 items-center">
                          <div className="w-52">
                            <DatePickerInput
                              value={endDate}
                              onChange={handleEndDateChange}
                              placeholder="End date"
                              min={(startDate && startDate > "2024-01-01") ? startDate : "2024-01-01"}
                              buttonClassName="!rounded-lg !border-gray-300 !bg-white !text-stone-900"
                              disabled={compareOptionsIsLoading}
                            />
                          </div>
                        </div>
                      </>
                    )}
                    {type === "select-month" && (
                      <div className="flex flex-row gap-2 items-center">
                        <div className="w-52">
                          <MonthPickerInput
                            value={selectedMonth}
                            onChange={handleMonthChange}
                            placeholder="Pick a month"
                            min="2024-01"
                            max={format(new Date(), "yyyy-MM")}
                            buttonClassName="!rounded-lg !border-gray-300 !bg-white !text-stone-900"
                            disabled={compareOptionsIsLoading}
                          />
                        </div>
                      </div>
                    )}
                    {statementView === "standard" && <><span className="text-sm text-zinc-500">Compare to</span>
                    <div className="flex flex-row gap-2 items-center">
                      <div className="w-52">
                        <SelectDropdown
                          options={compareOptions || [
                            { value: "none", label: "None" },
                          ]}
                          value={compareType}
                          onChange={setCompareType}
                          selectClassname={"!bg-white !border-gray-300 !rounded-lg !text-stone-900"}
                          disabled={compareOptionsIsLoading}
                        />
                      </div>
                    </div></>}
                  </>
                )}
              </div>
              </div>
            )}
            {isMobile && (
              <div className="border-b border-gray-200 py-2">
                <div className="flex min-w-0 items-center gap-2">
                  <div className="min-w-0">
                    <SelectDropdown
                      options={viewMode === "snapshot" ? (snapshotPeriodOptions || []) : [
                        { value: "this-year-to-date", label: "This Year to Date" },
                        { value: "last-month", label: "Last Month" },
                        { value: "last-quarter", label: "Last Quarter" },
                        { value: "last-year", label: "Last Year" },
                        { value: "select-month", label: "Select Month" },
                        { value: "custom-dates", label: "Custom Dates" },
                      ]}
                      value={viewMode === "snapshot" ? snapshotType : type}
                      onChange={handleMobilePeriodTypeChange}
                      selectClassname="!bg-white !border-gray-300 !rounded-lg !text-stone-900"
                      mainWrapperClassname="w-40 min-w-0 shrink-0"
                      disabled={viewMode === "snapshot"
                        ? snapshotOptionsIsLoading || !snapshotPeriodOptions?.length
                        : compareOptionsIsLoading}
                    />
                  </div>
                  {activeDateRangeLabel && (
                    <span className="min-w-0 truncate text-sm text-zinc-500">
                      {activeDateRangeLabel}
                    </span>
                  )}
                </div>
                {viewMode === "snapshot" && !snapshotOptionsIsLoading && !snapshotOptions?.length && (
                  <div className="mt-2 text-sm text-gray-500">
                    No snapshots available yet. The monthly snapshot cron will populate this list after it runs.
                  </div>
                )}
              </div>
            )}
            {isMobile && (
              <Dialog
                open={Boolean(mobilePeriodDialogType)}
                onClose={() => setMobilePeriodDialogType(null)}
                className="relative z-50"
              >
                <DialogBackdrop className="fixed inset-0 bg-black/30" />
                <div className="fixed inset-0 flex items-start justify-center p-4 pt-40">
                  <DialogPanel className="w-full max-w-sm rounded-lg bg-white p-4 shadow-xl">
                    <DialogTitle className="text-base font-semibold text-stone-900">
                      {mobilePeriodDialogType === "select-month" ? "Select Month" : "Custom Dates"}
                    </DialogTitle>
                    {mobilePeriodDialogType === "select-month" ? (
                      <div className="mt-3 w-full">
                        <MonthPickerInput
                          value={mobileDraftMonth}
                          onChange={setMobileDraftMonth}
                          placeholder="Pick a month"
                          min="2024-01"
                          max={format(new Date(), "yyyy-MM")}
                          buttonClassName="!w-full !rounded-lg !border-gray-300 !bg-white !text-stone-900"
                          disabled={compareOptionsIsLoading}
                        />
                      </div>
                    ) : (
                      <div className="mt-3 grid w-full min-w-0 grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2">
                        <DatePickerInput
                          value={mobileDraftStartDate}
                          onChange={handleMobileDraftStartDateChange}
                          placeholder="Start date"
                          min={viewMode === "snapshot" ? minSnapshotDate : "2024-01-01"}
                          max={mobileDraftEndDate || undefined}
                          allowedDates={viewMode === "snapshot" ? availableSnapshotDates : undefined}
                          buttonClassName="!w-full !rounded-lg !border-gray-300 !bg-white !text-stone-900"
                          disabled={viewMode === "snapshot" ? snapshotOptionsIsLoading : compareOptionsIsLoading}
                        />
                        <span className="text-sm text-zinc-500">to</span>
                        <DatePickerInput
                          value={mobileDraftEndDate}
                          onChange={handleMobileDraftEndDateChange}
                          placeholder="End date"
                          min={mobileDraftStartDate || (viewMode === "snapshot" ? minSnapshotDate : "2024-01-01")}
                          max={viewMode === "snapshot" ? snapshotEndPickerMax : undefined}
                          allowedDates={viewMode === "snapshot" ? availableSnapshotDates : undefined}
                          buttonClassName="!w-full !rounded-lg !border-gray-300 !bg-white !text-stone-900"
                          disabled={viewMode === "snapshot" ? snapshotOptionsIsLoading : compareOptionsIsLoading}
                        />
                      </div>
                    )}
                    <div className="mt-4 flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setMobilePeriodDialogType(null)}
                        className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-stone-900 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleMobilePeriodDialogApply}
                        disabled={
                          mobilePeriodDialogType === "select-month"
                            ? !mobileDraftMonth || compareOptionsIsLoading
                            : !mobileDraftStartDate || !mobileDraftEndDate
                              || (viewMode === "snapshot" ? snapshotOptionsIsLoading : compareOptionsIsLoading)
                        }
                        className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-800 disabled:cursor-not-allowed disabled:bg-gray-300"
                      >
                        Apply
                      </button>
                    </div>
                  </DialogPanel>
                </div>
              </Dialog>
            )}
            {statementView === "legacy" ? (
              <section className="relative my-6">
                {standardStatementLoading ? (
                  <div className="flex min-h-96 items-center justify-center">
                    <LoaderSpinner colorSpinner="black" width="70" height="70" border="5" />
                  </div>
                ) : standardStatementError ? (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                    {standardStatementError}
                  </div>
                ) : standardStatement && standardPayloadHasData(standardStatement) ? (
                  <>
                    {totalsCards}
                    <ProfitAndLossStatementTable payload={standardStatement} />
                  </>
                ) : standardStatement ? (
                  <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-sm text-zinc-500">
                    No data for the selected period.
                  </div>
                ) : null}
              </section>
            ) : (
              <>
            {totalsCards}
            <section className="relative h-full my-6">
                {profitAndLossNewDataIsLoading ? (
                  <div className="h-5/6 w-full absolute mt-8">
                      <LoaderSpinner colorSpinner="black" width="70" height="70" border="5" />
                  </div>
                ) : (
                  <div className="min-w-0 max-w-full overflow-hidden">
                    <div className="min-w-0 space-y-8">
                      {tableData?.["summary"] && (
                        <ProfitLossTable
                          key={`${tableTitleType}_${tableTitleStartDate}_${tableTitleEndDate}_${compareType}_summary`}
                          type={tableTitleType}
                          compareType={viewMode === "snapshot" ? "none" : compareType}
                          title={getProfitAndLossNewTableTitle("summary")}
                          data={tableData["summary"]}
                          acceptSidebarData={handleOpenBreakdownSidebar}
                          totalsByRows={totalsByRows}
                          onExport={handleExport}
                          isExporting={exportingTable === "summary"}
                          tableKey="summary"
                          compact
                        />
                      )}
                      {tableData?.["total-cost-of-goods-sold"] && (
                        <ProfitLossTable
                          key={`${tableTitleType}_${tableTitleStartDate}_${tableTitleEndDate}_${compareType}_total-cost-of-goods-sold`}
                          type={tableTitleType}
                          compareType={viewMode === "snapshot" ? "none" : compareType}
                          title={getProfitAndLossNewTableTitle("total-cost-of-goods-sold")}
                          data={tableData["total-cost-of-goods-sold"]}
                          acceptSidebarData={handleOpenBreakdownSidebar}
                          totalsByRows={totalsByRows}
                          onExport={handleExport}
                          isExporting={exportingTable === "total-cost-of-goods-sold"}
                          tableKey="total-cost-of-goods-sold"
                          compact
                        />
                      )}
                      {tableData?.["total-operating-expenses"] && (
                        <ProfitLossTable
                          key={`${tableTitleType}_${tableTitleStartDate}_${tableTitleEndDate}_${compareType}_total-operating-expenses`}
                          type={tableTitleType}
                          compareType={viewMode === "snapshot" ? "none" : compareType}
                          title={getProfitAndLossNewTableTitle("total-operating-expenses")}
                          data={tableData["total-operating-expenses"]}
                          acceptSidebarData={handleOpenBreakdownSidebar}
                          totalsByRows={totalsByRows}
                          onExport={handleExport}
                          isExporting={exportingTable === "total-operating-expenses"}
                          tableKey="total-operating-expenses"
                          compact
                        />
                      )}
                      {tableData?.["net-income-summary"] && (
                        <ProfitLossTable
                          key={`${tableTitleType}_${tableTitleStartDate}_${tableTitleEndDate}_${compareType}_net-income-summary`}
                          type={tableTitleType}
                          compareType={viewMode === "snapshot" ? "none" : compareType}
                          title={getProfitAndLossNewTableTitle("net-income-summary")}
                          data={tableData["net-income-summary"]}
                          acceptSidebarData={handleOpenBreakdownSidebar}
                          totalsByRows={totalsByRows}
                          onExport={handleExport}
                          isExporting={exportingTable === "net-income-summary"}
                          tableKey="net-income-summary"
                          compact
                        />
                      )}
                    </div>
                  </div>
                )}
            </section>
              </>
            )}
        </main>
        {sidebarData && (
            <ProfitReportSidebar
                sidebarData={sidebarData}
                onClose={() => setSidebarData(null)}
            />
        )}
        {settingsIsOpen && (
          <ProfitAndLossSettingsSidebar
            isOpen={settingsIsOpen}
            onClose={() => {
              setSettingsIsOpen(false);
              if (viewMode === "snapshot") {
                refreshSnapshotData();
              } else if (statementView === "legacy") {
                refreshStandardStatement();
              } else {
                refreshProfitAndLossData();
              }
            }}
          />
        )}
      </>

    )
};

export default ProfitAndLossNew;